import { createSelector } from '@reduxjs/toolkit';
import { TStoreState } from 'redux/types';
import {
	TAnnualFeeTypeDetails,
	TAnnualFeeTypeListResponse,
	TAnnualFeeTypeOption,
	TEasFeeManagementRequest,
	TEasFeeStore,
	TFeeClassDetails,
	TFeeClassListResponse,
	TFeeClassOption,
	TInvoicingFeeTypeDetails,
	TInvoicingFeeTypeListResponse,
	TInvoicingFeeTypeOption,
	TMonthlyFeeDetails,
	TMonthlyFeeDetailsListResponse,
	TMonthlyFeeOption,
} from './types';

export const getEasFeeState = (state: TStoreState): TEasFeeStore => state.easFee;

// ========================================== Annual fee types

export const getAnnualFeeTypeOptions = createSelector(
	[getEasFeeState],
	(easFee: TEasFeeStore): TAnnualFeeTypeOption[] | null => easFee.annualFeeTypeOptions,
);

export const getAnnualFeeTypeOptionsLoading = createSelector(
	[getEasFeeState],
	(easFee: TEasFeeStore): boolean => easFee.annualFeeTypeOptionsLoading,
);

export const getAnnualFeeTypeList = createSelector(
	[getEasFeeState],
	(easFee: TEasFeeStore): TAnnualFeeTypeListResponse | null => easFee.annualFeeTypeList,
);

export const getAnnualFeeTypeListLoading = createSelector(
	[getEasFeeState],
	(easFee: TEasFeeStore): boolean => easFee.annualFeeTypeListLoading,
);

export const getAnnualFeeTypeLastListParams = createSelector(
	[getEasFeeState],
	(easFee: TEasFeeStore): TEasFeeManagementRequest | null => easFee.annualFeeTypeLastListParams,
);

export const getAnnualFeeTypeDetails = createSelector(
	[getEasFeeState],
	(easFee: TEasFeeStore): TAnnualFeeTypeDetails | null => easFee.annualFeeTypeDetails,
);

export const getAnnualFeeTypeDetailsLoading = createSelector(
	[getEasFeeState],
	(easFee: TEasFeeStore): boolean => easFee.annualFeeTypeDetailsLoading,
);

export const getAnnualFeeTypeSaveLoading = createSelector(
	[getEasFeeState],
	(easFee: TEasFeeStore): boolean => easFee.annualFeeTypeSaveLoading,
);

export const getAnnualFeeTypeSaveSuccess = createSelector(
	[getEasFeeState],
	(easFee: TEasFeeStore): boolean => easFee.annualFeeTypeSaveSuccess,
);

export const getAnnualFeeTypeDeleteLoading = createSelector(
	[getEasFeeState],
	(easFee: TEasFeeStore): boolean => easFee.annualFeeTypeDeleteLoading,
);

// ========================================== Monthly fees

export const getMonthlyFeeOptions = createSelector(
	[getEasFeeState],
	(easFee: TEasFeeStore): TMonthlyFeeOption[] | null => easFee.monthlyFeeOptions,
);

export const getMonthlyFeeOptionsLoading = createSelector(
	[getEasFeeState],
	(easFee: TEasFeeStore): boolean => easFee.monthlyFeeOptionsLoading,
);

export const getMonthlyFeeDetailsList = createSelector(
	[getEasFeeState],
	(easFee: TEasFeeStore): TMonthlyFeeDetailsListResponse | null => easFee.monthlyFeeList,
);

export const getMonthlyFeeDetailsListLoading = createSelector(
	[getEasFeeState],
	(easFee: TEasFeeStore): boolean => easFee.monthlyFeeListLoading,
);

export const getMonthlyFeeLastListParams = createSelector(
	[getEasFeeState],
	(easFee: TEasFeeStore): TEasFeeManagementRequest | null => easFee.monthlyFeeLastListParams,
);

export const getMonthlyFeeDetails = createSelector(
	[getEasFeeState],
	(easFee: TEasFeeStore): TMonthlyFeeDetails | null => easFee.monthlyFeeDetails,
);

export const getMonthlyFeeDetailsLoading = createSelector(
	[getEasFeeState],
	(easFee: TEasFeeStore): boolean => easFee.monthlyFeeDetailsLoading,
);

export const getMonthlyFeeSaveLoading = createSelector(
	[getEasFeeState],
	(easFee: TEasFeeStore): boolean => easFee.monthlyFeeSaveLoading,
);

export const getMonthlyFeeSaveSuccess = createSelector(
	[getEasFeeState],
	(easFee: TEasFeeStore): boolean => easFee.monthlyFeeSaveSuccess,
);

export const getMonthlyFeeDeleteLoading = createSelector(
	[getEasFeeState],
	(easFee: TEasFeeStore): boolean => easFee.monthlyFeeDeleteLoading,
);

// ========================================== Invoicing fee types

export const getInvoicingFeeTypeOptions = createSelector(
	[getEasFeeState],
	(easFee: TEasFeeStore): TInvoicingFeeTypeOption[] | null => easFee.invoicingFeeTypeOptions,
);

export const getInvoicingFeeTypeOptionsLoading = createSelector(
	[getEasFeeState],
	(easFee: TEasFeeStore): boolean => easFee.invoicingFeeTypeOptionsLoading,
);

export const getInvoicingFeeTypeList = createSelector(
	[getEasFeeState],
	(easFee: TEasFeeStore): TInvoicingFeeTypeListResponse | null => easFee.invoicingFeeTypeList,
);

export const getInvoicingFeeTypeListLoading = createSelector(
	[getEasFeeState],
	(easFee: TEasFeeStore): boolean => easFee.invoicingFeeTypeListLoading,
);

export const getInvoicingFeeTypeLastListParams = createSelector(
	[getEasFeeState],
	(easFee: TEasFeeStore): TEasFeeManagementRequest | null => easFee.invoicingFeeTypeLastListParams,
);

export const getInvoicingFeeTypeDetails = createSelector(
	[getEasFeeState],
	(easFee: TEasFeeStore): TInvoicingFeeTypeDetails | null => easFee.invoicingFeeTypeDetails,
);

export const getInvoicingFeeTypeDetailsLoading = createSelector(
	[getEasFeeState],
	(easFee: TEasFeeStore): boolean => easFee.invoicingFeeTypeDetailsLoading,
);

export const getInvoicingFeeTypeSaveLoading = createSelector(
	[getEasFeeState],
	(easFee: TEasFeeStore): boolean => easFee.invoicingFeeTypeSaveLoading,
);

export const getInvoicingFeeTypeSaveSuccess = createSelector(
	[getEasFeeState],
	(easFee: TEasFeeStore): boolean => easFee.invoicingFeeTypeSaveSuccess,
);

// ========================================== Fee classes

export const getFeeClassOptions = createSelector(
	[getEasFeeState],
	(easFee: TEasFeeStore): TFeeClassOption[] | null => easFee.feeClassOptions,
);

export const getFeeClassOptionsLoading = createSelector(
	[getEasFeeState],
	(easFee: TEasFeeStore): boolean => easFee.feeClassOptionsLoading,
);

export const getFeeClassList = createSelector(
	[getEasFeeState],
	(easFee: TEasFeeStore): TFeeClassListResponse | null => easFee.feeClassList,
);

export const getFeeClassListLoading = createSelector(
	[getEasFeeState],
	(easFee: TEasFeeStore): boolean => easFee.feeClassListLoading,
);

export const getFeeClassLastListParams = createSelector(
	[getEasFeeState],
	(easFee: TEasFeeStore): TEasFeeManagementRequest | null => easFee.feeClassLastListParams,
);

export const getFeeClassDetails = createSelector(
	[getEasFeeState],
	(easFee: TEasFeeStore): TFeeClassDetails | null => easFee.feeClassDetails,
);

export const getFeeClassDetailsLoading = createSelector(
	[getEasFeeState],
	(easFee: TEasFeeStore): boolean => easFee.feeClassDetailsLoading,
);

export const getFeeClassSaveLoading = createSelector(
	[getEasFeeState],
	(easFee: TEasFeeStore): boolean => easFee.feeClassSaveLoading,
);

export const getFeeClassSaveSuccess = createSelector(
	[getEasFeeState],
	(easFee: TEasFeeStore): boolean => easFee.feeClassSaveSuccess,
);

export const getFeeClassDeleteLoading = createSelector(
	[getEasFeeState],
	(easFee: TEasFeeStore): boolean => easFee.feeClassDeleteLoading,
);
